/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from "react";
import { css, useTheme } from "@emotion/react";
import { Container, Icon, Link, Toggle } from "@coreai/component-library";
import exclamation from "../../../../assets/exclamation.svg";
import PositiveRepResultSection from "./PositiveRepResultSection";
import { useLanguage } from "LanguageContext";
import { useAnalysis } from "../FormComponents/AnalysisResultContext";
import { TFeedbackDetails } from "../../../../services/feedbackService";
import Tabs from "./PositiveRepTabs";

interface DetailedProps {
  resultData: any;
}

export interface SubPrinciples {
  sub_index: number;
  title: string;
  category: number;
  evaluation: string;
}
export interface AnalysisSummaryResult {
  title: string;
  evaluation?: string;
  feedbacks?: Array<TFeedbackDetails>;
  id?: string;
  index: number;
  content: string;
  category?: number;
  sub_principles: Array<SubPrinciples>;
}

export default function PositiveRepReferenceCard(props: Readonly<DetailedProps>) {
  const { resultData } = props;
  const { analysisContextResult } = useAnalysis();
  const theme = useTheme();
  const { t } = useLanguage();
  const [showToggle, setShowToggle] = useState(false);
  const [openAllSections, setOpenAllSections] = useState<Record<string, boolean>>({
    diversity: false,
    lifestyle: false,
  });

  const getBgColor = (cat: number) => {
    switch (cat) {
      case -1:
        return "#D3D7D9";
      case 0:
        return "#F84241";
      case 1:
        return "#15CB4A";
      case 3:
        return "#A3CDED";
      default:
        return "#FFC400";
    }
  };

  const toggleSectionAccordion = (sectionKey: string) => {
    setOpenAllSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const cardStyles = css`
    display: flex;
    flex-direction: column;
    // gap: 48px;
    width: 100%;
    box-sizing: border-box;
    background-color: ${theme.colors.grey};
    border-radius: 8px;
  `;

  const containerStyle = css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    padding: ${theme.spacing["5xl"]};
    text-align: left;
    border-radius: ${theme.radius.sm};
    color: ${theme.colors.textMedium};

    @media (max-width: 768px) {
      padding: 24px;
    }
  `;

  const criteriaWrapperStyle = css`
    &:not(:last-child) {
      margin-bottom: 64px;
    }
  `;

  const headerStyle = css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: ${theme.spacing.xxxl};

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
    }
  `;

  const titleWrapperStyles = css`
    display: flex;
    flex-direction: column;
  `;

  const titleStyles1 = css`
    font-family: ${theme.typography.subbody_medium.fontFamily};
    font-size: ${theme.typography.subbody_medium.fontSize};
    font-weight: ${theme.typography.heading4_semiBold.fontWeight};
    color: #607A8C;
    text-transform: uppercase;
    margin-top: 0px;
    margin-bottom: 8px;
  `;

  const titleStyles2 = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: 40px;
    font-weight: ${theme.typography.heading4_semiBold.fontWeight};
    color: ${theme.colors.textMedium};
    margin-top: 0px;
    line-height: 120%;
  `;

  const linkStyles = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    text-decoration: none;
    color: ${theme.colors.feedbackTitleColor};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;

    @media (max-width: 768px) {
      align-self: flex-end;
    }
  `;

  const toggleWrapper = css`
    display: flex;
    justify-content: right;
    align-items: flex-end;
    gap: 10px;
  `;

  const toggleStyle = css`
    align-self: flex-start;
  `;

  const toggleTitleStyle = css`
    font-family: ${theme.typography.subbody_medium.fontFamily};
    font-size: ${theme.typography.subbody_medium.fontSize};
    font-weight: 500;
    color: ${theme.colors.primary};
  `;

  const supportTextStyle = css`
    font-family: ${theme.typography.subbody_medium.fontFamily};
    font-size: ${theme.typography.subbody_medium.fontSize};
    font-weight: 400;
    color: ${theme.colors.textMedium};
  `;

  const updatedResponse = useMemo(() => {
    const feedbackMap = resultData?.responses?.reduce((acc: any, response: any) => {
      acc[Number(response.id)] = response.feedbacks || [];
      return acc;
    }, {});

    return resultData?.response?.map((group: any) => {
      return {
        ...group,
        domains: group.domains.map((domain: any) => {
          return {
            ...domain,
            principles: domain.principles.map((principle: any) => {
              const feedbacks = feedbackMap?.[principle.id] || [];
              return {
                ...principle,
                feedbacks,
              };
            }),
          };
        }),
      };
    });
  }, [resultData]);

  return (
    <Container variant="column" css={containerStyle}>
      <Tabs analysis={updatedResponse}/>

      {updatedResponse?.length > 0 && updatedResponse?.map((group: any) => (
      <div key={group.group_name} css={criteriaWrapperStyle}>
        <div css={headerStyle}>
          <div css={titleWrapperStyles}>
            <p css={titleStyles1}>
              {t("analyse_result_detailedAnalysis_title", null, analysisContextResult?.locale_code)}
            </p>
            <p css={titleStyles2}>{group?.group_name}</p>
          </div>
          <Link css={linkStyles} onClick={() => toggleSectionAccordion(group.group_name)}>
            {openAllSections[group.group_name] ? t("positiveRep_close_all_criteria", null, analysisContextResult?.locale_code) : t("positiveRep_show_all_criteria", null, analysisContextResult?.locale_code)}
            <Icon name={openAllSections[group.group_name] ? "chevron-up" : "chevron-down"} size="sm" />
          </Link>
        </div>

        <div css={cardStyles}>
        {group.domains.map((domain: any) => (
          <PositiveRepResultSection
            key={domain.domain_name}
            title={domain.domain_name}
            icon={<img src={exclamation} alt="alert" />}
            bgColor={getBgColor(domain?.category)}
            items={domain.principles}
            openAll={openAllSections[group.group_name]}
            showToggle={showToggle}
          />
        ))}
        </div>
      </div>
      ))}

      {/* Toggle for Details */}
      <div css={toggleWrapper}>
        <div css={toggleStyle}>
          <Toggle
            checked={showToggle}
            onCheckedChange={() => setShowToggle((prev) => !prev)}
            variant="brand"
            size="sm"
          />
        </div>
        <div>
          <div css={toggleTitleStyle}>{t("positiveRep_show_all_details", null, analysisContextResult?.locale_code)}</div>
          <div css={supportTextStyle}>{t("positiveRep_show_rationale", null, analysisContextResult?.locale_code)}</div>
        </div>
      </div>
    </Container>
  );
}
